import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { sortNewsByPublished } from "../../utils/newsSort";
import { getSourceKey, getSourceMapValue } from "../../utils/sourceModel";
import FocusPagerPage, {
  DEFAULT_INITIAL_BATCH_SIZE,
  FOCUS_RENDER_RADIUS,
  getFocusPagerItemKey,
} from "./FocusPagerPage";
import "./FocusPager.css";
import {
  dispatchFocusPagerPosition,
} from "../../utils/focusPagerProgress";

const FocusPagerIOS = ({
  sources = [],
  focusNewsBySource = {},
  favicons = {},
  onMarkAsRead,
  onNewsOpen,
  onSourceChange,
  initialSource,
  scrollToTopSignal,
  onActivePageAtTopChange,
  savedLinks,
  onToggleSaved,
  onShare,
  onView,
  onLoadMore,
  onEnsureInitial,
  hasMoreForSource,
  isLoadingMoreSource,
  hasCheckedInitialForSource,
  hasFailedInitialForSource,
  onRetryInitial,
  initialBatchSize = DEFAULT_INITIAL_BATCH_SIZE,
}) => {
  const containerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const isTouchingRef = useRef(false);
  const internalPageChangeRef = useRef(false);

  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, sources.indexOf(initialSource))
  );

  const activeIndexRef = useRef(activeIndex);

  const orderedSources = useMemo(() => [...sources].reverse(), [sources]);

  const loopedSources = useMemo(() => {
    if (orderedSources.length <= 1) return orderedSources;

    return [
      orderedSources[orderedSources.length - 1],
      ...orderedSources,
      orderedSources[0],
    ];
  }, [orderedSources]);

  const newsBySource = useMemo(() => {
    const grouped = new Map();
    const seenBySource = new Map();

    const sourceByKey = new Map();

    sources.forEach((source) => {
      grouped.set(source, []);
      seenBySource.set(source, new Set());
      sourceByKey.set(getSourceKey(source), source);
    });

    const pushUnique = (item, index) => {
      const canonicalSource = sourceByKey.get(getSourceKey(item?.source));

      if (!canonicalSource) return;

      const key = getFocusPagerItemKey(item, index);
      const seen = seenBySource.get(canonicalSource);

      if (seen.has(key)) return;

      seen.add(key);
      grouped.get(canonicalSource).push(item);
    };


    Object.values(focusNewsBySource).forEach((items) => {
      if (!Array.isArray(items)) return;
      items.forEach(pushUnique);
    });

    grouped.forEach((items, source) => {
      grouped.set(source, sortNewsByPublished(items));
    });

    return grouped;
  }, [focusNewsBySource, sources]);

  const scrollToIndex = useCallback(
    (index) => {
      const container = containerRef.current;
      if (!container) return;

      const source = sources[index];
      const visualIndex = orderedSources.indexOf(source);

      if (visualIndex < 0) return;

      const loopedVisualIndex =
        orderedSources.length > 1 ? visualIndex + 1 : visualIndex;

      container.scrollLeft = loopedVisualIndex * container.clientWidth;
    },
    [orderedSources, sources]
  );

  useLayoutEffect(() => {
    const initialIndex = sources.indexOf(initialSource);
    const container = containerRef.current;

    if (initialIndex < 0 || !container) return undefined;

    if (
      internalPageChangeRef.current &&
      initialIndex === activeIndexRef.current
    ) {
      internalPageChangeRef.current = false;
      return undefined;
    }

    activeIndexRef.current = initialIndex;
    setActiveIndex(initialIndex);

    const visualIndex = orderedSources.indexOf(initialSource);
    const loopedVisualIndex =
      orderedSources.length > 1 ? visualIndex + 1 : visualIndex;

    const setScrollPosition = () => {
      container.scrollLeft = loopedVisualIndex * container.clientWidth;

      dispatchFocusPagerPosition(
        loopedVisualIndex,
        loopedSources
      );
    };

    setScrollPosition();

    const frameId =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(setScrollPosition)
        : null;

    return () => {
      if (frameId !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(frameId);
      }
    };
  }, [
    initialSource,
    loopedSources,
    orderedSources,
    sources,
  ]);

  const shouldRenderPage = useCallback(
    (index) => {
      if (index < 0 || sources.length <= 1) return true;

      const directDistance = Math.abs(index - activeIndex);
      const loopDistance = sources.length - directDistance;

      return Math.min(directDistance, loopDistance) <= FOCUS_RENDER_RADIUS;
    },
    [activeIndex, sources.length]
  );

  const activateVisualIndex = useCallback(
    (visualIndex) => {
      const source = loopedSources[visualIndex];
      const newIndex = sources.indexOf(source);

      if (
        newIndex < 0 ||
        newIndex >= sources.length ||
        newIndex === activeIndexRef.current
      ) {
        return;
      }

      activeIndexRef.current = newIndex;
      setActiveIndex(newIndex);
      onActivePageAtTopChange?.(true);

      internalPageChangeRef.current = true;
      onSourceChange?.(sources[newIndex]);
    },
    [loopedSources, onActivePageAtTopChange, onSourceChange, sources]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const clearScrollEndTimer = () => {
      if (!scrollTimeoutRef.current) return;

      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    };

    const handleScrollEnd = () => {
      /*
      |--------------------------------------------------------------------
      | Never settle while the finger is still on the iOS pager.
      |--------------------------------------------------------------------
      |
      | A slow swipe can legitimately pause for more than 120ms between
      | scroll events while the user's finger is still touching the screen.
      | Treating that pause as scroll-end rounds the fractional position to
      | the previous/next page and makes the Focus ticker capsule jump.
      |--------------------------------------------------------------------
      */
      if (isTouchingRef.current) return;

      const pageWidth = container.clientWidth;
      if (pageWidth === 0) return;

      const visualIndex = Math.round(container.scrollLeft / pageWidth);

      dispatchFocusPagerPosition(visualIndex, loopedSources);
      activateVisualIndex(visualIndex);

      if (orderedSources.length <= 1) return;

      const jumpToLoopPosition = () => {
        if (visualIndex === 0) {
          container.scrollLeft = orderedSources.length * pageWidth;
        } else if (visualIndex === loopedSources.length - 1) {
          container.scrollLeft = pageWidth;
        }
      };

      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(jumpToLoopPosition);
      } else {
        jumpToLoopPosition();
      }
    };

    const scheduleScrollEnd = () => {
      clearScrollEndTimer();

      if (isTouchingRef.current) return;

      scrollTimeoutRef.current = setTimeout(() => {
        scrollTimeoutRef.current = null;
        handleScrollEnd();
      }, 120);
    };

    const handleTouchStart = () => {
      isTouchingRef.current = true;
      clearScrollEndTimer();
    };

    const handleTouchEnd = () => {
      isTouchingRef.current = false;

      /*
       * Native momentum / scroll-snap may continue after touchend. Any
       * following scroll event resets this timer, so commit happens only
       * after the pager has genuinely gone quiet.
       */
      scheduleScrollEnd();
    };

    const handleScroll = () => {
      clearScrollEndTimer();

      const pageWidth = container.clientWidth;

      if (pageWidth > 0) {
        /*
        |--------------------------------------------------------------------
        | Do NOT commit the next source while the iOS pager is still moving.
        |--------------------------------------------------------------------
        |
        | During scroll we publish only the physical fractional position.
        | focusSource is committed after touch release AND after native
        | momentum / scroll-snap has stopped.
        |--------------------------------------------------------------------
        */
        dispatchFocusPagerPosition(
          container.scrollLeft / pageWidth,
          loopedSources
        );
      }

      scheduleScrollEnd();
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });
    container.addEventListener("touchcancel", handleTouchEnd, {
      passive: true,
    });
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
      container.removeEventListener("scroll", handleScroll);

      isTouchingRef.current = false;
      clearScrollEndTimer();
    };
  }, [activateVisualIndex, loopedSources, orderedSources.length]);

  useEffect(() => {
    const initialIndex = sources.indexOf(initialSource);

    if (initialIndex >= 0 && initialIndex !== activeIndex) {
      activeIndexRef.current = initialIndex;
      setActiveIndex(initialIndex);
      scrollToIndex(initialIndex);
    }
  }, [initialSource, sources, activeIndex, scrollToIndex]);

  if (sources.length === 0) return null;

  return (
    <div className="focus-pager focus-pager--ios">
      <div className="focus-pager__container" ref={containerRef}>
        {loopedSources.map((source, visualIndex) => {
          const index = sources.indexOf(source);
          const sourceNews = newsBySource.get(source) || [];
          const focusBatchCount = getSourceMapValue(
            focusNewsBySource,
            source,
            []
          ).length;
          const isPageRendered = shouldRenderPage(index);
          const isActivePage = index === activeIndex;

          const sourceVisualIndex = orderedSources.indexOf(source);
          const canonicalVisualIndex =
            orderedSources.length > 1
              ? sourceVisualIndex + 1
              : sourceVisualIndex;

          const isCanonicalPage = visualIndex === canonicalVisualIndex;

          const isSourceLoading = Boolean(
            isLoadingMoreSource?.(source)
          );

          const hasSourceCheckedInitial = Boolean(
            hasCheckedInitialForSource?.(source)
          );

          const hasSourceInitialError = Boolean(
            hasFailedInitialForSource?.(source)
          );

          return (
            <FocusPagerPage
              key={`${source}-${visualIndex}`}
              className={`focus-pager__page ${
                !isPageRendered ? "focus-pager__page--skeleton" : ""
              }`}
              sourceKey={getSourceKey(source)}
              sourceNews={sourceNews}
              isPageRendered={isPageRendered}
              isActivePage={isActivePage}
              favicons={favicons}
              onMarkAsRead={onMarkAsRead}
              onNewsOpen={onNewsOpen}
              scrollToTopSignal={isActivePage ? scrollToTopSignal : 0}
              onAtTopChange={
                isActivePage ? onActivePageAtTopChange : undefined
              }
              savedLinks={savedLinks}
              onToggleSaved={onToggleSaved}
              onShare={onShare}
              onView={onView}
              onLoadMore={
                isActivePage
                  ? () => onLoadMore?.(source, sourceNews)
                  : undefined
              }
              onEnsureInitial={
                isCanonicalPage
                  ? () => onEnsureInitial?.(source, sourceNews)
                  : undefined
              }
              hasMore={isActivePage ? hasMoreForSource?.(source) : false}
              isLoadingMore={isSourceLoading}
              hasCheckedInitial={hasSourceCheckedInitial}
              hasInitialError={hasSourceInitialError}
              onRetryInitial={
                isCanonicalPage
                  ? () => onRetryInitial?.(source, sourceNews)
                  : undefined
              }
              initialBatchSize={initialBatchSize}
              focusBatchCount={focusBatchCount}
            />
          );
        })}
      </div>
    </div>
  );
};

export default FocusPagerIOS;
